import React from "react";
import { Link } from "react-router-dom";
import { Bot, Globe, Code2, Clapperboard, ArrowUpRight, ChevronDown } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Container, ButtonLink, Section, SectionHeading, CtaBand } from "../components/site";
import { Reveal, CountUp } from "../components/motion";
import { WorkShowcase, Testimonials } from "../components/Work";
import { testimonials } from "../data/work";
import { useI18n } from "../i18n";

const serviceCards = [
  { id: "ai-automation", icon: Bot },
  { id: "web-development", icon: Globe },
  { id: "python-automation", icon: Code2 },
  { id: "ai-video", icon: Clapperboard },
];

// Floating capability tags around the hero lines (positions in %).
const tagSpots = [
  { top: "22%", left: "6%", delay: "0s" },
  { top: "14%", left: "60%", delay: "-1.5s" },
  { top: "44%", left: "84%", delay: "-3s" },
  { top: "74%", left: "8%", delay: "-2s" },
  { top: "82%", left: "72%", delay: "-4s" },
  { top: "64%", left: "88%", delay: "-5s" },
];

const HeroLines = ({ tags }) => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute left-1/2 top-1/3 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/10 blur-3xl" />
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
    >
      <path
        className="draw-path"
        style={{ "--len": 2000, "--delay": "200ms" }}
        d="M-60 640 C 260 380, 560 760, 880 470 S 1320 180, 1520 300"
        stroke="rgb(var(--brand))"
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      <path
        className="draw-path"
        style={{ "--len": 2000, "--delay": "500ms" }}
        d="M-60 260 C 240 140, 520 380, 820 300 S 1300 520, 1520 430"
        stroke="rgb(var(--brand))"
        strokeOpacity="0.3"
        strokeWidth="1.2"
      />
      <path
        className="draw-path"
        style={{ "--len": 2200, "--delay": "800ms" }}
        d="M180 960 C 360 640, 700 620, 980 760 S 1380 900, 1520 700"
        stroke="rgb(var(--ink))"
        strokeOpacity="0.12"
        strokeWidth="1"
      />
    </svg>
    {tags.map((tag, i) => (
      <span
        key={tag}
        className={`float absolute items-center gap-2 rounded-full border border-line bg-surface/80 px-3 py-1.5 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-2 shadow-sm backdrop-blur ${
          i > 3 ? "hidden xl:inline-flex" : "hidden md:inline-flex"
        }`}
        style={{ top: tagSpots[i].top, left: tagSpots[i].left, "--delay": tagSpots[i].delay }}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-brand" />
        {tag}
      </span>
    ))}
  </div>
);

const Home = () => {
  const { t } = useI18n();
  const stats = t("home.stats");
  const steps = t("home.process.steps");
  const marquee = t("home.marquee");

  return (
    <>
      <Header />

      {/* 1. Hero */}
      <section className="relative flex min-h-[100svh] items-center overflow-hidden pt-28 pb-24">
        <HeroLines tags={t("home.hero.tags")} />
        <Container className="relative text-center">
          <div className="rise flex justify-center">
            <span className="inline-flex items-center gap-2.5 rounded-full border border-line bg-surface/80 px-4 py-2 text-sm text-ink-2 backdrop-blur">
              <span className="pulse-dot h-2 w-2 rounded-full bg-brand" />
              {t("home.hero.eyebrow")}
            </span>
          </div>
          <h1 className="mx-auto mt-8 max-w-5xl font-display text-[2.9rem] leading-[1] tracking-[-0.02em] text-ink sm:text-7xl lg:text-8xl">
            <span className="rise block" style={{ "--delay": "120ms" }}>
              {t("home.hero.titleA")}
            </span>
            <span className="rise block italic text-brand" style={{ "--delay": "260ms" }}>
              {t("home.hero.titleB")}
            </span>
          </h1>
          <p
            className="rise mx-auto mt-8 max-w-xl text-lg leading-relaxed text-ink-2 md:text-xl"
            style={{ "--delay": "400ms" }}
          >
            {t("home.hero.lede")}
          </p>
          <div className="rise mt-10 flex flex-wrap justify-center gap-3" style={{ "--delay": "540ms" }}>
            <ButtonLink to="/checkout" size="lg" arrow>
              {t("home.hero.primary")}
            </ButtonLink>
            <a href="#work" className="btn btn-secondary btn-lg">
              {t("home.hero.secondary")}
            </a>
          </div>
        </Container>
        <a
          href="#services"
          className="rise absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 text-xs text-ink-3 transition-colors hover:text-ink"
          style={{ "--delay": "900ms" }}
        >
          {t("common.scroll")}
          <ChevronDown size={18} className="float" />
        </a>
      </section>

      {/* 2. Moving capability strip */}
      <div className="marquee relative overflow-hidden border-y border-line bg-surface py-5" aria-hidden="true">
        <div className="marquee-track flex w-max gap-10 whitespace-nowrap">
          {[...marquee, ...marquee].map((item, i) => (
            <span key={i} className="flex items-center gap-10 font-display text-2xl text-ink-2 md:text-3xl">
              {item}
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            </span>
          ))}
        </div>
      </div>

      {/* 3. Services */}
      <Section id="services" className="scroll-mt-16">
        <SectionHeading
          label={t("home.services.label")}
          title={t("home.services.title")}
          action={
            <ButtonLink to="/services" variant="secondary" arrow>
              {t("home.services.more")}
            </ButtonLink>
          }
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {serviceCards.map(({ id, icon: Icon }, i) => (
            <Reveal key={id} delay={i * 90}>
              <Link
                to={`/services#${id}`}
                className="card-lift group relative flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-surface p-7 md:p-9"
              >
                <span
                  aria-hidden="true"
                  className="absolute -end-16 -top-16 h-40 w-40 rounded-full bg-brand/10 blur-2xl transition-transform duration-700 group-hover:scale-150"
                />
                <div className="relative flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                    <Icon size={22} />
                  </span>
                  <span className="font-mono text-xs text-ink-3">0{i + 1}</span>
                </div>
                <h3 className="relative mt-10 text-2xl font-semibold tracking-tight text-ink">
                  {t(`services.items.${id}.title`)}
                </h3>
                <p className="relative mt-2 text-[0.9375rem] leading-relaxed text-ink-2">
                  {t(`services.items.${id}.tagline`)}
                </p>
                <span className="relative mt-8 inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-ink transition-all duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-on-brand">
                  <ArrowUpRight size={16} className="rtl:-scale-x-100" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 4. Numbers */}
      <section className="border-y border-line bg-surface">
        <Container className="grid divide-y divide-line sm:grid-cols-3 sm:divide-x sm:divide-y-0 rtl:sm:divide-x-reverse">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 100} className="py-10 text-center md:py-14">
              <div className="font-display text-6xl text-ink md:text-7xl">
                <CountUp value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-2 text-sm text-ink-2">{s.label}</div>
            </Reveal>
          ))}
        </Container>
      </section>

      {/* 5. Work */}
      <Section id="work" className="scroll-mt-16">
        <SectionHeading label={t("home.work.label")} title={t("home.work.title")} intro={t("home.work.intro")} />
        <WorkShowcase />
        {testimonials.length > 0 && (
          <div className="mt-24">
            <SectionHeading label={t("home.testimonials.label")} title={t("home.testimonials.title")} />
            <Testimonials />
          </div>
        )}
      </Section>

      {/* 6. Process */}
      <Section className="pt-0 md:pt-0">
        <SectionHeading label={t("home.process.label")} title={t("home.process.title")} center />
        <ol className="relative grid gap-10 md:grid-cols-4 md:gap-6">
          <Reveal
            variant="fade"
            aria-hidden="true"
            className="absolute inset-x-[12%] top-6 hidden h-px bg-gradient-to-r from-transparent via-brand/60 to-transparent md:block"
          />
          {steps.map((s, i) => (
            <Reveal as="li" key={s.title} delay={i * 140} className="relative text-center">
              <span className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-brand/30 bg-paper font-mono text-sm text-brand shadow-[0_0_0_6px_rgb(var(--paper))]">
                {i + 1}
              </span>
              <h3 className="mt-5 text-lg font-semibold text-ink">{s.title}</h3>
              <p className="mx-auto mt-1.5 max-w-[14rem] text-[0.9375rem] text-ink-2">{s.desc}</p>
            </Reveal>
          ))}
        </ol>
        <Reveal className="mt-14 flex justify-center">
          <ButtonLink to="/contact" arrow>
            {t("cta.primary")}
          </ButtonLink>
        </Reveal>
      </Section>

      {/* 7. Call to action → Contact */}
      <CtaBand />
      <Footer />
    </>
  );
};

export default Home;
